import { apiService } from 'core/services/apiService';
import { paginationService } from 'core/services/paginationService';
import { IWorkplaces } from 'module/workplaces/interface/WorkplacesInterface';
import { IWorkplacesAll } from 'module/preload/interfaces/WorkplacesAllInterface';
import { IGuestProtect } from 'module/guestprotect/interface/IGuestProtect';
import { IGuestConnectSettings } from 'module/guestconnect/interface/IGuestConnectSettings';
import { IGuestConnectPost } from 'module/guestconnect/interface/IGuestConnectPost';
import { IGuestProtectPostResponse } from 'module/guestconnect/interface/IGuestPostResponse';
import { IguestCheck } from 'module/guestconnect/interface/IGuestCheck';
import { TimeZone } from '../../types';

/**
 * Fetches the list of workplaces accessible to the current authenticated user.
 * @returns An {@link IWorkplaces} object containing an `items` array of workplace records.
 */
const GetWorkplaceList = async (): Promise<IWorkplaces> => {
	const data = apiService.responseHandler(await apiService.get(`users/workplaces`));
	// console.log('workplaces data', data);
	return { items: Object.values(data) || [] };
};

/**
 * Fetches all workplaces with full details (all pages).
 */
const GetAllWorkplaces = async (): Promise<any[]> => {
	const data = await paginationService.getWithHandler('workplaces');
	return data?.member || data?.['hydra:member'] || [];
};

/**
 * Normalises a timezone object from any API shape into a UTC offset in minutes.
 *
 * Handles multiple formats:
 * - Numeric fields (`offset`, `offset_minutes`, etc.) as hours or minutes
 * - String formats: `+11:00`, `-07`, `+11`
 * - Label strings containing UTC/GMT patterns: `"(UTC-10:00)"`, `"GMT+2"`
 *
 * @param tz - A raw timezone object from the API.
 * @returns The UTC offset in minutes (e.g. `330` for IST, `-300` for EST).
 */
const parseOffsetToMinutes = (tz: any): number => {
	// 1) Direct numeric offset fields (could be number of hours or minutes or string like "+11:00" or "-07")
	const rawCandidates = [tz?.offset, tz?.offset_minutes, tz?.utc_offset, tz?.utcOffset, tz?.offsetMinutes, tz?.utcOffsetMinutes];
	for (const raw of rawCandidates) {
		if (!raw && raw !== 0) continue;
		const s = raw.toString();

		// If it's a plain number representing minutes or hours
		if (/^-?\d+$/.test(s)) {
			const n = parseInt(s, 10);
			// Heuristic: if magnitude > 24 assume minutes, else hours
			if (Math.abs(n) > 24) return n; // minutes
			return n * 60; // hours
		}

		// Matches formats like +11, -07, +11:00, -07:30
		const m = s.match(/([+-]?)(\d{1,2})(?::(\d{2}))?/);
		if (m) {
			const sign = m[1] === '-' ? -1 : 1;
			const hours = parseInt(m[2], 10) || 0;
			const minutes = parseInt(m[3] || '0', 10) || 0;
			return sign * (hours * 60 + minutes);
		}
	}

	const label = (tz?.label || tz?.name || '').toString();
	if (label) {
		// 2) Match explicit UTC/GMT patterns anywhere in the label: "UTC-10:00", "(UTC-10)", "GMT+2"
		const g = label.match(/(?:UTC|GMT)\s*([+-]?\d{1,2})(?::(\d{2}))?/i);
		if (g) {
			const sign = g[1].startsWith('-') ? -1 : 1;
			const hours = parseInt(g[1].replace('+', ''), 10) || 0;
			const minutes = parseInt(g[2] || '0', 10) || 0;
			return sign * (hours * 60 + minutes);
		}

		// 3) Generic sign+hours anywhere in label, e.g. "(UTC-10:00)", "-10:00"
		const any = label.match(/([+-])(\d{1,2})(?::(\d{2}))?/);
		if (any) {
			const sign = any[1] === '-' ? -1 : 1;
			const hours = parseInt(any[2], 10) || 0;
			const minutes = parseInt(any[3] || '0', 10) || 0;
			return sign * (hours * 60 + minutes);
		}

		// 4) Handle 'Etc/GMT+10' style where the sign is inverted: 'Etc/GMT+10' means UTC-10
		const etc = label.match(/Etc\/GMT([+-]?\d{1,2})/i);
		if (etc) {
			const val = parseInt(etc[1], 10) || 0;
			// invert sign
			return -1 * val * 60;
		}
	}

	// Default to 0 minutes if we can't determine
	return 0;
};

const GetTimeZones = async (): Promise<TimeZone[]> => {
	const data = apiService.responseHandler(await apiService.get(`/time_zones`));

	const list: TimeZone[] = data?.member || [];

	// Sort by numeric offset descending so east (positive) offsets come before west (negative).
	list.sort((a: TimeZone, b: TimeZone) => {
		const offA = parseOffsetToMinutes(a as any);
		const offB = parseOffsetToMinutes(b as any);

		if (offA === offB) {
			// Stable secondary sort by label
			return (a.label || '').localeCompare(b.label || '');
		}
		return offB - offA; // descending
	});
	// console.log("sorted timezones", list);

	return list;
};

const GetAllWorkplaceList = async (): Promise<IWorkplacesAll> => {
	const { ...data } = apiService.responseHandler(await apiService.get(`users/workplaces`));

	return data;
};

const GetSingleWorkplace = async (workplaceId: number): Promise<any> => {
	const { ...data } = apiService.responseHandler(await apiService.get(`workplaces/${workplaceId}`));

	return data;
};

const GetGuestProtect = async (workplaceId: string): Promise<IGuestProtect> => {
	const { ...data } = apiService.responseHandler(
		await apiService.get(`workplaces/guest-connect/${workplaceId}`)
	);

	return data;
};

const PostGuestProtect = async (config: {
	workplaceId: string;
	body: any;
}): Promise<IGuestProtect> => {
	const { ...data } = apiService.responseHandler(
		await apiService.post(`workplaces/guest-connect/${config.workplaceId}`, config.body)
	);

	return data;
};

const GetGuestConnect = async (workplaceId: string): Promise<IGuestConnectSettings> => {
	const { member } = apiService.responseHandler(
		await apiService.get(`guest_connect_settings?workplace=${workplaceId}`)
	);

	return member;
};

const PostGuestConnect = async ({
	workplaceIdentifier,
	domainIdentifier,
	body,
}: {
	workplaceIdentifier: string;
	domainIdentifier: string;
	body: Partial<IGuestConnectPost>;
}): Promise<IGuestProtectPostResponse> => {
	const { ...data } = apiService.responseHandler(
		await apiService.post(`guest-connect/${workplaceIdentifier}/${domainIdentifier}`, body)
	);

	return data;
};
const CheckGuestConnect = async (identifier: string): Promise<IguestCheck> => {
	const { ...data } = apiService.responseHandler(
		await apiService.get(`guest-connect/check/${identifier}`)
	);

	return data;
};

const PutGuestConnect = async (identifier: string): Promise<any> => {
	const { ...data } = apiService.responseHandler(
		await apiService.put(`guest-connect/reward/${identifier}`, {})
	);

	return data;
};

const CreateCampaign = async (payload: any): Promise<any> => {
	const data = apiService.responseHandler(
		await apiService.post(`guest_connect_feedback_campaigns`, payload)
	);
	return data;
};

const UpdateCampaign = async (campaignId: string, payload: any): Promise<any> => {
	const data = apiService.responseHandler(
		await apiService.put(`guest_connect_feedback_campaigns/${campaignId}`, payload)
	);
	return data;
};

const GetCampaigns = async (workplaceId?: string, itemsPerPage: number = 30): Promise<any> => {
	const endpoint = workplaceId 
		? `guest_connect_feedback_campaigns?workplace=${workplaceId}`
		: `guest_connect_feedback_campaigns`;
	const data = await paginationService.getWithHandler(endpoint, undefined, itemsPerPage);
	return data; // Returns full response with all items combined
};

const GetCampaignSubmissions = async (workplaceId: string, itemsPerPage: number = 50): Promise<any> => {
	const data = await paginationService.getWithHandler(
		`guest_connect_feedback_submissions?workplace=${workplaceId}`,
		undefined,
		itemsPerPage
	);
	return data?.member || data?.['hydra:member'] || [];
};

const GetRewards = async (workplaceId?: string): Promise<any> => {
	const endpoint = workplaceId 
		? `guest_connect_rewards?workplace=${workplaceId}`
		: `guest_connect_rewards`;
	const data = await paginationService.getWithHandler(endpoint);
	return data?.member || data?.['hydra:member'] || [];
};

const GetOrganizations = async (): Promise<any> => {
	const data = await paginationService.getWithHandler(`organizations`);
	return data?.member || data?.['hydra:member'] || [];
};

const GetGuestSentimentAnalyses = async (workplaceId?: string): Promise<any> => {
	const endpoint = workplaceId 
		? `guest_connect_sentiment_analyses?workplace=${workplaceId}`
		: `guest_connect_sentiment_analyses`;
	const data = await paginationService.getWithHandler(endpoint);
	return data?.member || data?.['hydra:member'] || [];
};

const GetFeedbackWinners = async (workplaceId?: string): Promise<any> => {
	const endpoint = workplaceId 
		? `guest_connect_feedback_winners?workplace=${workplaceId}`
		: `guest_connect_feedback_winners`;
	const data = await paginationService.getWithHandler(endpoint);
	return data?.member || data?.['hydra:member'] || [];
};

const GetFeedbackWinnerById = async (winnerId: string): Promise<any> => {
	const data = apiService.responseHandler(
		await apiService.get(`guest_connect_feedback_winners/${winnerId}`)
	);
	return data;
};

const PickCampaignWinners = async (campaignId: string): Promise<any> => {
	const data = apiService.responseHandler(
		await apiService.post(`feedback/campaign/${campaignId}/pick-winners`, {})
	);
	return data;
};

const DeleteCampaign = async (campaignId: string): Promise<any> => {
	const data = apiService.responseHandler(
		await apiService.delete(`guest_connect_feedback_campaigns/${campaignId}`)
	);
	return data;
};

export {
	GetWorkplaceList,
	GetAllWorkplaces,
	GetAllWorkplaceList,
	GetSingleWorkplace,
	GetGuestProtect,
	PostGuestProtect,
	GetGuestConnect,
	PostGuestConnect,
	CheckGuestConnect,
	PutGuestConnect,
	CreateCampaign,
	UpdateCampaign,
	DeleteCampaign,
	GetCampaigns,
	GetCampaignSubmissions,
	GetRewards,
	GetOrganizations,
	GetTimeZones,
	GetGuestSentimentAnalyses,
	GetFeedbackWinners,
	GetFeedbackWinnerById,
	PickCampaignWinners,
};
